import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/formatDate';
import {
  RefreshCw, Search, ArrowLeft, AlertCircle, CheckCircle, Clock, X,
  Ban, Activity, CreditCard, FileText,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n || 0);

const STATUS_META = {
  created:  { label: 'Создан',     color: 'var(--text-3)' },
  paid:     { label: 'Оплачен',    color: 'var(--green)'  },
  printing: { label: 'Печатается', color: 'var(--brand)'  },
  done:     { label: 'Завершён',   color: 'var(--green)'  },
  failed:   { label: 'Ошибка',     color: 'var(--red)'    },
};

// Подкатегории отменённых заказов. На сервере оба — status='failed',
// различаются по error_reason. Синтетические значения фильтра совпадают
// с теми, что понимает GET /api/dashboard/orders.
const CANCEL_META = {
  cancelled_manual: { label: 'Отменён вручную',          color: 'var(--yellow)' },
  cancelled_auto:   { label: 'Авто-отмена (не оплачен)', color: 'var(--text-3)' },
};

const isManualCancel = (o) => o.status === 'failed' && /^Отменён администратором/.test(o.error_reason || '');
const isAutoCancel   = (o) => o.status === 'failed' && /^Auto-cancelled/.test(o.error_reason || '');

const SOURCE_LABELS = {
  phone:    'С телефона',
  phone_tg: 'С Telegram',
  usb:      'USB',
  scan:     'Сканер/Копия',
};

// Способ оплаты. Multicard — основной провайдер, Click — legacy-фолбэк.
// Источник правды — payment_provider: оно проставляется на сервере ТОЛЬКО при
// реальном подтверждении оплаты. Идентификаторы (click_trans_id / multicard_uuid)
// — лишь фолбэк для старых заказов без payment_provider: важно, что multicard_uuid
// мог быть проставлен и неоплаченному заказу, поэтому click_trans_id первее.
const PAYMENT_META = {
  click:     { label: 'Click',     sub: null,     color: 'var(--brand)' },
  multicard: { label: 'Multicard', sub: null, color: 'var(--green)' },
};

const providerOf = (o) =>
  o.payment_provider
  || (o.click_trans_id ? 'click' : (o.multicard_uuid ? 'multicard' : null));

/* ── Toast ───────────────────────────────────────────────────────────────── */
const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
    {type === 'info'    && <Clock       size={14} style={{ color: 'var(--brand)', flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

/* ── Cancel payment confirmation modal ───────────────────────────────────── */
const CancelModal = ({ order, onConfirm, onCancel, loading }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
    <div className="card" style={{ width: '380px', padding: '28px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--red)' }}>
        <Ban size={17} /> Отменить платёж?
      </h3>
      <p style={{ fontSize: '13px', marginBottom: '6px' }}>
        Заказ: <strong className="mono">{order.order_id}</strong>
      </p>
      <p style={{ fontSize: '13px', marginBottom: '6px' }}>
        Сумма: <strong>{fmt(order.total_amount)} UZS</strong>
      </p>
      <p style={{ fontSize: '13px', marginBottom: '6px' }}>
        Провайдер: <strong>{PAYMENT_META[providerOf(order)]?.label || '—'}</strong>
      </p>
      <p className="text-dim" style={{ fontSize: '12px', marginBottom: '24px', lineHeight: 1.5 }}>
        Будет вызван возврат через платёжный шлюз. Клиенту вернут деньги, заказ перейдёт в статус «Ошибка». Действие необратимо.
      </p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel} disabled={loading}>Назад</button>
        <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
          <Ban size={13} /> {loading ? 'Отмена...' : 'Отменить платёж'}
        </button>
      </div>
    </div>
  </div>
);

/* ── Order detail modal ──────────────────────────────────────────────────── */
const Field = ({ label, children, mono }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
    <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>{label}</span>
    <span className={mono ? 'mono' : ''} style={{ fontSize: '13px', color: 'var(--text-1)', wordBreak: 'break-all' }}>
      {children}
    </span>
  </div>
);

const OrderDetailModal = ({ order, onClose }) => {
  const provider = providerOf(order);
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
    >
      <div className="card" onClick={e => e.stopPropagation()} style={{ width: '440px', maxWidth: '94vw', padding: '24px', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={16} style={{ color: 'var(--brand)' }} /> Детали заказа
          </h3>
          <button className="btn-icon" style={{ width: '28px', height: '28px', border: 'none' }} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        {/* Файл и сессия — то, ради чего открывается окно */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '14px', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', marginBottom: '16px' }}>
          <Field label="Имя файла">
            {order.file_name
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <FileText size={13} style={{ color: 'var(--brand)', flexShrink: 0 }} />{order.file_name}
                </span>
              : <span className="text-dim">— нет файла (скан/копия)</span>}
          </Field>
          <Field label="Сессия загрузки" mono>
            {order.session_id || <span className="text-dim" style={{ fontFamily: 'inherit' }}>— без сессии (USB/скан)</span>}
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <Field label="ID заказа" mono>{order.order_id}</Field>
          <Field label="Создан">{fmtDate(order.created_at)}</Field>
          <Field label="Статус"><StatusPill status={order.status} /></Field>
          <Field label="Источник">{SOURCE_LABELS[order.source] || order.source}</Field>
          <Field label="Печать">{order.is_duplex ? 'Двусторонняя' : 'Односторонняя'}</Field>
          <Field label="Страниц / листов">
            {`${fmt(order.page_count)} стр. → ${fmt(order.sheets)} л. × ${fmt(order.copies)} коп.`}
          </Field>
          <Field label="Сумма">{`${fmt(order.total_amount)} UZS`}</Field>
          <Field label="Оплата">{provider ? (PAYMENT_META[provider]?.label || provider) : '—'}</Field>
        </div>

        {order.error_reason && (
          <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--red)', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
            <AlertCircle size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ lineHeight: 1.4 }}>{order.error_reason}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Status pill ─────────────────────────────────────────────────────────── */
const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: 'var(--text-3)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: '600',
      background: 'rgba(255,255,255,0.04)', color: meta.color,
      border: `1px solid ${meta.color}33`,
    }}>
      <span className="dot" style={{ background: meta.color, width: 6, height: 6 }} />
      {meta.label}
    </span>
  );
};

/* ── Payment method cell ─────────────────────────────────────────────────── */
const PaymentCell = ({ order }) => {
  const method = providerOf(order);
  if (!method) return <span className="text-dim" style={{ fontSize: '11px' }}>—</span>;

  const meta  = PAYMENT_META[method];
  const txId  = method === 'click' ? order.click_trans_id : order.multicard_uuid;
  const txStr = txId != null ? String(txId) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: '600',
        background: 'rgba(255,255,255,0.04)', color: meta.color,
        border: `1px solid ${meta.color}33`,
      }}>
        <CreditCard size={11} />
        {meta.label}
        {meta.sub && <span style={{ opacity: 0.65, fontWeight: '500' }}>· {meta.sub}</span>}
      </span>
      {txStr && (
        <span className="mono text-dim" style={{ fontSize: '10px' }} title={txStr}>
          {txStr.length > 18 ? txStr.slice(0, 18) + '…' : txStr}
        </span>
      )}
    </div>
  );
};

/* ── Order row ───────────────────────────────────────────────────────────── */
const OrderRow = ({ order, onCancel, onOpen }) => {
  // onCancel = null у профильных сессий — отмена платежа доступна только админу
  const provider = providerOf(order);
  const canCancel = !!onCancel
    && ['paid', 'printing', 'done'].includes(order.status)
    && !!provider;

  return (
    <tr onClick={() => onOpen(order)} style={{ cursor: 'pointer' }} title="Открыть детали заказа">
      <td>
        <div className="mono" style={{ fontSize: '12px', fontWeight: '600' }}>{order.order_id}</div>
        <div className="text-dim" style={{ fontSize: '11px', marginTop: '2px' }}>
          {fmtDate(order.created_at)}
        </div>
      </td>
      <td>
        <StatusPill status={order.status} />
        {order.error_reason && (
          <div style={{
            fontSize: '11px', marginTop: '4px', color: 'var(--red)',
            display: 'flex', alignItems: 'flex-start', gap: '4px', maxWidth: '260px',
          }}>
            <AlertCircle size={11} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={{ lineHeight: 1.4 }}>{order.error_reason}</span>
          </div>
        )}
      </td>
      <td>
        <div style={{ fontSize: '12px' }}>
          {SOURCE_LABELS[order.source] || order.source} · {order.is_duplex ? 'Двустор.' : 'Одностор.'}
        </div>
        <div className="text-dim" style={{ fontSize: '11px', marginTop: '2px' }}>
          {order.copies > 1
            ? `${fmt(order.page_count)} стр. → ${fmt(order.sheets)} л./коп. × ${fmt(order.copies)} коп. = ${fmt(order.sheets * order.copies)} л.`
            : `${fmt(order.page_count)} стр. → ${fmt(order.sheets)} л.`}
        </div>
      </td>
      <td>
        <div style={{ fontWeight: '600' }}>{fmt(order.total_amount)}</div>
        <div className="text-dim" style={{ fontSize: '11px' }}>UZS</div>
      </td>
      <td>
        <PaymentCell order={order} />
      </td>
      <td>
        <button
          className="btn-icon"
          title={canCancel
            ? `Отменить платёж через ${PAYMENT_META[provider]?.label || provider}`
            : 'Отмена недоступна'}
          disabled={!canCancel}
          onClick={(e) => { e.stopPropagation(); canCancel && onCancel(order); }}
          style={{
            width: 'auto', padding: '4px 10px', borderRadius: '6px',
            gap: '4px', fontSize: '11px', display: 'flex', alignItems: 'center',
            color: canCancel ? 'var(--red)' : 'var(--text-3)',
            opacity: canCancel ? 1 : 0.4,
          }}
        >
          <Ban size={12} />
          Отмена
        </button>
      </td>
    </tr>
  );
};

/* ── Device orders view ──────────────────────────────────────────────────── */
const DeviceOrdersView = ({ device, onBack, addToast }) => {
  const { fetchAuthorized, isAdmin } = useAuth();
  const [orders, setOrders]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('');
  const [offset, setOffset]       = useState(0);
  const [toCancel, setToCancel]   = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [detail, setDetail]       = useState(null);
  const LIMIT = 50;

  const fetchPage = useCallback(async (pageOffset, append) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        device_id: device.device_id,
        limit:     String(LIMIT),
        offset:    String(pageOffset),
      });
      if (statusFilter) params.set('status', statusFilter);

      const res  = await fetchAuthorized(`/api/dashboard/orders?${params}`);
      const data = await res.json();
      if (data.success) {
        setOrders(prev => append ? [...prev, ...data.orders] : data.orders);
        setTotal(data.total);
        setOffset(pageOffset + LIMIT);
      }
    } catch {
      addToast('Ошибка загрузки заказов', 'error');
    } finally {
      setLoading(false);
    }
  }, [device.device_id, statusFilter, fetchAuthorized, addToast]);

  // Reload when filter/device changes
  useEffect(() => { fetchPage(0, false); }, [fetchPage]);

  const reload    = () => fetchPage(0, false);
  const loadMore  = () => fetchPage(offset, true);

  const handleCancelConfirm = async () => {
    if (!toCancel) return;
    setCancelling(true);
    try {
      const res  = await fetchAuthorized(`/api/dashboard/orders/${toCancel.order_id}/cancel-payment`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        addToast(`Платёж отменён через ${data.provider === 'multicard' ? 'Multicard' : 'Click'}`, 'success');
        const reason = data.provider === 'multicard'
          ? 'Отменён администратором (Multicard refund OK)'
          : 'Отменён администратором (Click reversal OK)';
        setOrders(prev => prev.map(o =>
          o.order_id === toCancel.order_id
            ? { ...o, status: 'failed', error_reason: reason }
            : o
        ));
        setToCancel(null);
      } else {
        addToast(data.error || 'Click отклонил отмену', 'error');
      }
    } catch (err) {
      addToast('Ошибка соединения', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const counts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const cancelCounts = {
    cancelled_manual: orders.filter(isManualCancel).length,
    cancelled_auto:   orders.filter(isAutoCancel).length,
  };

  return (
    <div className="fade-in">
      {toCancel && (
        <CancelModal
          order={toCancel}
          loading={cancelling}
          onConfirm={handleCancelConfirm}
          onCancel={() => !cancelling && setToCancel(null)}
        />
      )}

      {detail && (
        <OrderDetailModal order={detail} onClose={() => setDetail(null)} />
      )}

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            className="btn-icon"
            onClick={onBack}
            title="Назад к списку киосков"
            style={{ width: '36px', height: '36px' }}
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1>{device.name || device.machine_name || 'Без имени'}</h1>
            <p>
              <span className="mono">{device.device_id?.slice(0, 14)}</span>
              {device.location && <> · 📍 {device.location}</>}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={e => setStatus(e.target.value)}
            style={{
              height: '32px', fontSize: '13px', borderRadius: '8px', padding: '0 10px',
              background: 'var(--bg-card)', color: 'var(--text-1)',
              border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
            }}
          >
            <option value="">Все статусы</option>
            {Object.entries(STATUS_META).map(([s, m]) => (
              <option key={s} value={s}>{m.label}</option>
            ))}
            <optgroup label="Отменённые">
              {Object.entries(CANCEL_META).map(([s, m]) => (
                <option key={s} value={s}>{m.label}</option>
              ))}
            </optgroup>
          </select>
          <button className="btn-icon" onClick={reload} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      <div className="orders-stat-grid">
        <div className="card stat-card">
          <div className="stat-value" style={{ fontSize: '20px' }}>{fmt(total)}</div>
          <div className="stat-label">Всего загружено</div>
        </div>
        {Object.entries(STATUS_META).map(([s, m]) => (
          <div key={s} className="card stat-card">
            <div className="stat-value" style={{ fontSize: '20px', color: m.color }}>{fmt(counts[s] || 0)}</div>
            <div className="stat-label">{m.label}</div>
          </div>
        ))}
        {/* Два блока отменённых заказов — кликабельны, фильтруют таблицу */}
        {Object.entries(CANCEL_META).map(([s, m]) => (
          <div
            key={s}
            className="card stat-card"
            onClick={() => setStatus(statusFilter === s ? '' : s)}
            title={`Показать только: ${m.label}`}
            style={{
              cursor: 'pointer',
              border: statusFilter === s ? `1px solid ${m.color}` : undefined,
            }}
          >
            <div className="stat-value" style={{ fontSize: '20px', color: m.color }}>{fmt(cancelCounts[s] || 0)}</div>
            <div className="stat-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th>Заказ / Дата</th>
              <th>Статус</th>
              <th>Тип</th>
              <th>Сумма</th>
              <th>Оплата</th>
              <th style={{ width: '110px' }}></th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? (
              orders.map(o => <OrderRow key={o.order_id} order={o} onCancel={isAdmin ? setToCancel : null} onOpen={setDetail} />)
            ) : (
              <tr>
                <td colSpan={6} className="empty">
                  {loading ? 'Загрузка...' : 'Заказов нет'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {orders.length < total && (
          <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button className="btn" onClick={loadMore} disabled={loading}>
              Загрузить ещё ({total - orders.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Device card (block) ─────────────────────────────────────────────────── */
const DeviceCard = ({ device, onClick }) => {
  const totalRev = device.revenue_30d || 0;
  return (
    <button
      onClick={() => onClick(device)}
      className="card"
      style={{
        padding: '18px', textAlign: 'left', cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.08)', background: 'var(--bg-card)',
        transition: 'transform 0.12s var(--ease), border-color 0.12s var(--ease)',
        display: 'flex', flexDirection: 'column', gap: '10px',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {device.name || device.machine_name || 'Без имени'}
          </div>
          <div className="text-dim mono" style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Activity size={9} /> {device.device_id?.slice(0, 14)}
          </div>
          {device.location && (
            <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              📍 {device.location}
            </div>
          )}
          {device.profile_id && (
            <div style={{ marginTop: '3px' }}>
              <span style={{
                fontSize: '10px', padding: '1px 7px', borderRadius: '20px',
                background: 'rgba(129,140,248,0.14)', color: 'var(--brand)', fontWeight: 600,
              }}>
                👤 {device.profile_display_name || device.profile_username}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}>
          <span className={`dot ${device.is_online ? 'dot-online' : 'dot-offline'}`} />
          <span style={{ color: device.is_online ? 'var(--green)' : 'var(--text-3)' }}>
            {device.is_online ? 'Онлайн' : 'Оффлайн'}
          </span>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px',
        paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '2px' }}>Заказов / 30д</div>
          <div style={{ fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FileText size={12} style={{ color: 'var(--brand)' }} />
            {fmt(device.orders_30d)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '2px' }}>Выручка / 30д</div>
          <div style={{ fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CreditCard size={12} style={{ color: 'var(--yellow)' }} />
            {fmt(totalRev)}
            <span style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: '500' }}>UZS</span>
          </div>
        </div>
      </div>
    </button>
  );
};

/* ── Main ────────────────────────────────────────────────────────────────── */
export const Orders = () => {
  const { fetchAuthorized, isAdmin } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [profileFilter, setProfileFilter] = useState('all'); // all | none | <id> (admin)
  const [selected, setSelected] = useState(null);
  const [toasts, setToasts]     = useState([]);
  const toastId = useRef(0);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetchAuthorized('/api/dashboard/devices');
      const data = await res.json();
      if (data.success) setDevices(data.devices);
    } catch { addToast('Ошибка загрузки устройств', 'error'); }
    finally { setLoading(false); }
  }, [fetchAuthorized, addToast]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  // Список профилей для фильтра собираем из самих устройств (без лишнего запроса)
  const profileOptions = [];
  devices.forEach(d => {
    if (d.profile_id && !profileOptions.some(p => p.id === d.profile_id)) {
      profileOptions.push({ id: d.profile_id, label: d.profile_display_name || d.profile_username });
    }
  });

  const filtered = devices.filter(d => {
    if (profileFilter === 'none' && d.profile_id) return false;
    if (profileFilter !== 'all' && profileFilter !== 'none' && String(d.profile_id) !== profileFilter) return false;
    return (
      (d.device_id   || '').toLowerCase().includes(search.toLowerCase()) ||
      (d.machine_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (d.name         || '').toLowerCase().includes(search.toLowerCase()) ||
      (d.location     || '').toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <>
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))} />
        ))}
      </div>

      {selected ? (
        <DeviceOrdersView
          device={selected}
          onBack={() => setSelected(null)}
          addToast={addToast}
        />
      ) : (
        <div className="fade-in">
          <div className="page-header">
            <div>
              <h1>Заказы</h1>
              <p>Выберите киоск для просмотра истории заказов</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Фильтр по профилю — только для админа */}
              {isAdmin && (
                <select
                  value={profileFilter}
                  onChange={e => setProfileFilter(e.target.value)}
                  style={{ height: '32px', fontSize: '13px', borderRadius: '8px', padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text-1)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                >
                  <option value="all">Все киоски</option>
                  <option value="none">Только без профиля</option>
                  {profileOptions.map(p => (
                    <option key={p.id} value={String(p.id)}>👤 {p.label}</option>
                  ))}
                </select>
              )}
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
                <input
                  placeholder="Поиск киоска..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ width: '220px', paddingLeft: '32px' }}
                />
              </div>
              <button className="btn-icon" onClick={loadDevices} disabled={loading} title="Обновить">
                <RefreshCw size={14} className={loading ? 'spin' : ''} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="empty" style={{ padding: '40px 0' }}>Загрузка...</div>
          ) : filtered.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '12px',
            }}>
              {filtered.map(d => (
                <DeviceCard key={d.device_id} device={d} onClick={setSelected} />
              ))}
            </div>
          ) : (
            <div className="card empty" style={{ padding: '40px' }}>Киоски не найдены</div>
          )}
        </div>
      )}
    </>
  );
};
